/* eslint-disable react-hooks/exhaustive-deps */
import { firestore } from "@/helpers/firebase";
import {
  collection,
  doc,
  type DocumentData,
  type DocumentSnapshot,
  type FirestoreError,
  getDoc,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { type DependencyList, useEffect, useState } from "react";

export type FirestoreDocUpdateMode = "never" | "onDepsChange" | "onSnapshot";

const firestoreCache = new Map<
  string,
  DocumentSnapshot<DocumentData, DocumentData>
>();

export async function preloadDoc<T>(
  docPath: [string, ...string[]]
): Promise<T> {
  const [collectionPath, ...docPaths] = docPath;
  const docRef = doc(firestore, collectionPath, ...docPaths);

  if (firestoreCache.has(docRef.path)) {
    return firestoreCache.get(docRef.path)?.data() as T;
  }

  const docc = await getDoc(docRef);
  firestoreCache.set(docRef.path, docc);
  return docc.data() as T;
}

export async function preloadDocs<T>(
  collectionPath: [string, ...string[]]
): Promise<T[]> {
  const [cPath, ...cPathSegs] = collectionPath;
  const collectionRef = collection(firestore, cPath, ...cPathSegs);

  const docs = await getDocs(collectionRef);
  const result: T[] = [];
  for (const docc of docs.docs) {
    firestoreCache.set(docc.ref.path, docc);

    result.push(docc.data() as T);
  }

  return result;
}

export function useFirestoreDoc<T>(
  getDocPath: () => [string, ...string[]] | undefined,
  deps: DependencyList = [],
  updateMode: FirestoreDocUpdateMode = "onDepsChange"
): [T | undefined, boolean, FirestoreError | undefined] {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<FirestoreError>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const docPath = getDocPath();
    if (!docPath) {
      return;
    }
    const [collectionPath, ...docPaths] = docPath;
    const docRef = doc(firestore, collectionPath, ...docPaths);

    if (updateMode === "onSnapshot") {
      return onSnapshot(
        docRef,
        (doc) => {
          firestoreCache.set(docRef.path, doc);

          setData(doc.data() as T);
          setLoading(false);
          setError(undefined);
        },
        (error) => {
          setData(undefined);
          setLoading(false);
          setError(error);
        }
      );
    }
      if (updateMode === "never") {
        if (firestoreCache.has(docRef.path)) {
          setData(firestoreCache.get(docRef.path)?.data() as T);
          setLoading(false);
          setError(undefined);
          return;
        }
      }

      setLoading(true);
      setError(undefined);

      let isCurrent = true;

      getDoc(docRef)
        .then((doc) => {
          if (!isCurrent) {
            return;
          }

          firestoreCache.set(docRef.path, doc);

          setData(doc.data() as T);
          setLoading(false);
          setError(undefined);
        })
        .catch((error) => {
          // should probably show an error/handle with sentry/posthog

          if (!isCurrent) {
            return;
          }

          setData(undefined);
          setLoading(false);
          setError(error);
        });

      return () => {
        isCurrent = false;
      };
  }, [...deps, updateMode]);

  return [data, loading, error];
}
